'use client';

import React, { useState } from 'react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

interface DateRange {
  id: string;
  type: 'blocked' | 'available';
  label: string;
  start: string; // YYYY-MM-DD
  end: string;
  reason?: string;
}

interface Property {
  id: string;
  name: string;
  color: string;
}

const properties: Property[] = [
  { id: 'p1', name: 'Lakeside Villa', color: 'bg-rose-400' },
  { id: 'p2', name: 'Kampala Apartment', color: 'bg-blue-400' },
  { id: 'p3', name: 'Bwindi Lodge', color: 'bg-emerald-400' },
];

const initialRanges: DateRange[] = [
  { id: 'r1', type: 'blocked', label: 'Maintenance', start: '2026-08-05', end: '2026-08-07', reason: 'Plumbing repairs' },
  { id: 'r2', type: 'blocked', label: 'Personal Use', start: '2026-08-20', end: '2026-08-22', reason: 'Family visit' },
  { id: 'r3', type: 'available', label: 'Open for Booking', start: '2026-08-10', end: '2026-08-18' },
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}
function toDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function getDayRange(dateStr: string, ranges: DateRange[]) {
  for (const r of ranges) {
    if (dateStr >= r.start && dateStr <= r.end) return r;
  }
  return null;
}

export default function PropertyAvailability() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedProperty, setSelectedProperty] = useState('p1');
  const [ranges, setRanges] = useState<DateRange[]>(initialRanges);
  const [selectionStart, setSelectionStart] = useState<string | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newRangeType, setNewRangeType] = useState<'blocked' | 'available'>('blocked');
  const [newRangeLabel, setNewRangeLabel] = useState('');
  const [newRangeReason, setNewRangeReason] = useState('');
  const [activeTab, setActiveTab] = useState<'calendar' | 'ranges'>('calendar');

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleDayClick = (dateStr: string) => {
    if (!selectionStart) {
      setSelectionStart(dateStr);
      setSelectionEnd(null);
    } else if (!selectionEnd) {
      if (dateStr < selectionStart) {
        setSelectionEnd(selectionStart);
        setSelectionStart(dateStr);
      } else {
        setSelectionEnd(dateStr);
      }
      setShowModal(true);
    } else {
      setSelectionStart(dateStr);
      setSelectionEnd(null);
    }
  };

  const handleAddRange = () => {
    if (!selectionStart || !selectionEnd || !newRangeLabel.trim()) return;
    const newRange: DateRange = {
      id: `r${Date.now()}`,
      type: newRangeType,
      label: newRangeLabel.trim(),
      start: selectionStart,
      end: selectionEnd,
      reason: newRangeReason.trim() || undefined,
    };
    setRanges(prev => [...prev, newRange]);
    setShowModal(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setNewRangeLabel('');
    setNewRangeReason('');
  };

  const handleRemoveRange = (id: string) => {
    setRanges(prev => prev.filter(r => r.id !== id));
  };

  const isInSelection = (dateStr: string) => {
    if (!selectionStart) return false;
    if (!selectionEnd) return dateStr === selectionStart;
    return dateStr >= selectionStart && dateStr <= selectionEnd;
  };

  const blockedCount = ranges.filter(r => r.type === 'blocked').length;
  const availableCount = ranges.filter(r => r.type === 'available').length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Properties', value: properties.length.toString(), sub: 'Under management', icon: '🏠' },
          { label: 'Blocked Ranges', value: blockedCount.toString(), sub: 'Dates unavailable', icon: '🚫' },
          { label: 'Open Ranges', value: availableCount.toString(), sub: 'Explicitly available', icon: '✅' },
          { label: 'Min Stay', value: '2 nights', sub: 'Default setting', icon: '🌙' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{s.icon}</span>
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            </div>
            <div className="text-sm font-semibold text-gray-700">{s.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Property Selector */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {properties.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelectedProperty(p.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
              selectedProperty === p.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
            }`}
          >
            <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
            {p.name}
          </button>
        ))}
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
        {(['calendar', 'ranges'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold capitalize transition-all ${
              activeTab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'calendar' ? '📅 Calendar View' : '📋 Manage Ranges'}
          </button>
        ))}
      </div>

      {activeTab === 'calendar' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="font-bold text-gray-900">{MONTHS[month]} {year}</h3>
              <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-7 mb-2">
              {DAYS.map((d) => (
                <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = toDateStr(year, month, day);
                const range = getDayRange(dateStr, ranges);
                const inSel = isInSelection(dateStr);
                const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                const isPast = dateStr < toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

                let cellClass = 'hover:bg-gray-100 text-gray-700 cursor-pointer';
                if (isPast) cellClass = 'text-gray-300 cursor-default';
                else if (inSel) cellClass = 'bg-rose-200 text-rose-800 ring-2 ring-rose-400';
                else if (range?.type === 'blocked') cellClass = 'bg-red-100 text-red-600 cursor-pointer';
                else if (range?.type === 'available') cellClass = 'bg-green-100 text-green-700 cursor-pointer';

                return (
                  <div
                    key={day}
                    onClick={() => !isPast && handleDayClick(dateStr)}
                    className={`relative aspect-square flex items-center justify-center rounded-xl text-sm font-semibold transition-all
                      ${cellClass}
                      ${isToday && !range && !inSel ? 'ring-2 ring-rose-400 text-rose-500' : ''}
                    `}
                    title={range ? `${range.label}${range.reason ? ': ' + range.reason : ''}` : ''}
                  >
                    {day}
                    {range && (
                      <div className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${range.type === 'blocked' ? 'bg-red-400' : 'bg-green-500'}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100">
              {[
                { color: 'bg-red-100 border border-red-200', label: 'Blocked' },
                { color: 'bg-green-100 border border-green-200', label: 'Explicitly Available' },
                { color: 'bg-rose-200 border border-rose-300', label: 'Selected Range' },
                { color: 'ring-2 ring-rose-400', label: 'Today' },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className={`w-4 h-4 rounded-md ${l.color}`} />
                  <span className="text-xs text-gray-600">{l.label}</span>
                </div>
              ))}
            </div>

            {selectionStart && !selectionEnd && (
              <div className="mt-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5 text-sm text-rose-700 font-medium">
                📍 Start date selected: <strong>{selectionStart}</strong> — now click an end date
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Block */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => { setNewRangeType('blocked'); setSelectionStart(null); setSelectionEnd(null); }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl text-sm font-semibold text-red-700 transition-colors"
                >
                  <span>🚫</span> Block Dates
                </button>
                <button
                  onClick={() => { setNewRangeType('available'); setSelectionStart(null); setSelectionEnd(null); }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl text-sm font-semibold text-green-700 transition-colors"
                >
                  <span>✅</span> Mark Available
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-3">Click two dates on the calendar to select a range, then save.</p>
            </div>

            {/* Active Ranges */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-3">Active Rules</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {ranges.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No date rules set</p>
                )}
                {ranges.map((r) => (
                  <div key={r.id} className={`flex items-start gap-2 p-3 rounded-xl border ${r.type === 'blocked' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    <span className="text-base mt-0.5">{r.type === 'blocked' ? '🚫' : '✅'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{r.label}</p>
                      <p className="text-xs text-gray-500">{r.start} → {r.end}</p>
                      {r.reason && <p className="text-xs text-gray-400 truncate">{r.reason}</p>}
                    </div>
                    <button
                      onClick={() => handleRemoveRange(r.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 mt-0.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Ranges Management Table */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">All Date Rules</h3>
            <button
              onClick={() => setActiveTab('calendar')}
              className="text-sm font-semibold text-rose-600 hover:text-rose-700 transition-colors"
            >
              + Add via Calendar
            </button>
          </div>
          {ranges.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-3">📅</div>
              <p className="font-semibold">No date rules yet</p>
              <p className="text-sm mt-1">Use the calendar to block or open dates</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {ranges.map((r) => (
                <div key={r.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${r.type === 'blocked' ? 'bg-red-400' : 'bg-green-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{r.label}</p>
                    {r.reason && <p className="text-xs text-gray-400">{r.reason}</p>}
                  </div>
                  <div className="text-sm text-gray-500 font-medium">{r.start}</div>
                  <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <div className="text-sm text-gray-500 font-medium">{r.end}</div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.type === 'blocked' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                    {r.type}
                  </span>
                  <button
                    onClick={() => handleRemoveRange(r.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Range Modal */}
      {showModal && selectionStart && selectionEnd && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Set Date Rule</h3>
            <p className="text-sm text-gray-500 mb-5">
              {selectionStart} → {selectionEnd}
            </p>

            {/* Type Toggle */}
            <div className="flex gap-2 mb-4">
              {(['blocked', 'available'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setNewRangeType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize transition-all ${
                    newRangeType === t
                      ? t === 'blocked' ? 'bg-red-500 text-white' : 'bg-green-500 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t === 'blocked' ? '🚫 Block' : '✅ Open'}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Label *</label>
                <input
                  type="text"
                  value={newRangeLabel}
                  onChange={(e) => setNewRangeLabel(e.target.value)}
                  placeholder={newRangeType === 'blocked' ? 'e.g. Maintenance, Personal Use' : 'e.g. Open for Booking'}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={newRangeReason}
                  onChange={(e) => setNewRangeReason(e.target.value)}
                  placeholder="Add a note..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setShowModal(false); setSelectionStart(null); setSelectionEnd(null); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRange}
                disabled={!newRangeLabel.trim()}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
